import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { X, Loader, User, Phone, MapPin, Calendar, Activity, CreditCard } from 'lucide-react';

const EditCustomerModal = ({ customer, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    username: '',
    name: '',
    phone: '',
    cnic: '',
    address: '',
    status: 'active',
    expiry_date: '',
    package: null,
    area: null,
    discount_type: 'none',
    custom_price: '',
    due_balance: '',
    credit_balance: '',
  });
  
  const [packages, setPackages] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [error, setError] = useState(null);
  const [basePrice, setBasePrice] = useState(null);

  useEffect(() => {
    if (customer) {
      setFormData({
        username: customer.username,
        name: customer.name,
        phone: customer.phone,
        cnic: customer.cnic || '',
        address: customer.address || '',
        status: customer.status,
        package: customer.package,
        area: customer.area,       
        expiry_date: customer.expiry_date ? customer.expiry_date.split('T')[0] : '' ,
        discount_type: customer.discount_type || 'none',
        custom_price: customer.custom_price?.toString() || '',
        due_balance: customer.due_balance?.toString() || '0.00',
        credit_balance: customer.credit_balance?.toString() || '0.00',
      });
    }

    const fetchData = async () => {
      try {
        const [pkgRes, areaRes] = await Promise.all([
          api.get('/packages/'),
          api.get('/areas/'),
        ]);
        setPackages(pkgRes.data.results || []);
        setAreas(areaRes.data.results || []);
      } catch (err) {
        console.error("Error loading dropdowns:", err);
        setError('Failed to load packages and areas');
      } finally {
        setFetchingData(false);
      }
    };
    fetchData();
  }, [customer]);

  useEffect(() => {
    if (formData.package) {
      const pkg = packages.find(p => p.id === parseInt(formData.package));
      if (pkg) {
        setBasePrice(parseFloat(pkg.monthly_price));
      } else {
        setBasePrice(null);
      }
    } else {
      setBasePrice(null);
    }
  }, [formData.package, packages]);

  // 🔥 Calculate effective price
  const getEffectivePrice = () => {
    if (!basePrice) return null;
    
    switch (formData.discount_type) {
      case 'none': return basePrice;
      case 'percent_25': return basePrice * 0.75;
      case 'percent_50': return basePrice * 0.50;
      case 'percent_75': return basePrice * 0.25;
      case 'full': return 0;
      case 'custom': return formData.custom_price ? parseFloat(formData.custom_price) : basePrice;
      default: return basePrice;
    }
  };

  const effectivePrice = getEffectivePrice();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const payload = {
      ...formData,
      package: formData.package ? parseInt(formData.package) : null,
      area: formData.area ? parseInt(formData.area) : null,
    };

    try {
      await api.put(`/customers/${customer.id}/`, payload);
      onSave(); 
      onClose();
    } catch (err) {
      console.error("Update Error:", err.response?.data);
      setError(err.response?.data?.detail || 'Failed to update customer');
    } finally {
      setLoading(false);
    }
  };

  // Reusable Input Class
  const inputClass = "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-sm disabled:bg-gray-50";
  const labelClass = "block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide";

  if (fetchingData) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-8 flex items-center gap-3">
          <Loader className="animate-spin text-blue-600" size={24} />
          <span className="text-gray-700 font-medium">Loading data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto flex flex-col">
        
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 p-5 flex items-center justify-between z-10">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Edit Customer</h2>
            <p className="text-xs text-gray-500 mt-1">Update subscriber details and service status</p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-900 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <span className="text-red-600 text-sm font-medium">{error}</span>
            </div>
          )}

          {/* 🔥 Grid Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            
            {/* Row 1: Identity */}
            <div>
              <label className={labelClass}><User size={12} className="inline mr-1"/> Username</label>
              <input 
                type="text" readOnly className={`${inputClass} bg-gray-100 cursor-not-allowed`}
                value={formData.username}
              />
              <p className="text-[10px] text-gray-400 mt-1">Username is permanent</p>
            </div>
            <div>
              <label className={labelClass}>Full Name *</label>
              <input 
                type="text" required className={inputClass}
                value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})}
                disabled={loading}
              />
            </div>

            {/* Row 2: Contact */}
            <div>
              <label className={labelClass}><Phone size={12} className="inline mr-1"/> Phone *</label>
              <input 
                type="tel" required className={inputClass}
                value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})}
                disabled={loading}
              />
            </div>
            <div>
              <label className={labelClass}>CNIC</label>
              <input 
                type="text" className={inputClass}
                value={formData.cnic} onChange={(e) => setFormData({...formData, cnic: e.target.value})}
                disabled={loading}
              />
            </div>

            {/* Row 3: Address (Full Width) */}
            <div className="md:col-span-2">
              <label className={labelClass}><MapPin size={12} className="inline mr-1"/> Address</label>
              <textarea 
                rows="2" className={inputClass}
                value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})}
                disabled={loading} placeholder="Enter full installation address"
              />
            </div>

            {/* Row 4: Service Details */}
            <div>
              <label className={labelClass}>Package</label>
              <select 
                className={inputClass}
                value={formData.package || ''}
                onChange={(e) => setFormData({...formData, package: e.target.value})}
                disabled={loading}
              >
                <option value="">Select Package</option>
                {packages.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name} {p.bandwidth ? `- ${p.bandwidth}` : ''} - PKR {p.monthly_price}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Area</label>
              <select 
                className={inputClass}
                value={formData.area || ''}
                onChange={(e) => setFormData({...formData, area: e.target.value})}
                disabled={loading}
              >
                <option value="">Select Area</option>
                {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>

            {/* 🔥 Discount/Pricing Section (Only shows when package selected) */}
            {formData.package && basePrice !== null && (
              <div className="md:col-span-2 p-4 bg-purple-50 rounded-xl border border-purple-200">
                <h4 className="font-semibold text-purple-900 mb-3 flex items-center gap-2">
                  💰 Pricing & Discount
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Discount Type</label>
                    <select 
                      name="discount_type" 
                      value={formData.discount_type} 
                      onChange={(e) => setFormData({...formData, discount_type: e.target.value})}
                      className={inputClass}
                      disabled={loading}
                    >
                      <option value="none">No Discount (PKR {basePrice?.toFixed(2)})</option>
                      <option value="percent_25">25% Off (PKR {(basePrice * 0.75)?.toFixed(2)})</option>
                      <option value="percent_50">50% Off (PKR {(basePrice * 0.50)?.toFixed(2)})</option>
                      <option value="percent_75">75% Off (PKR {(basePrice * 0.25)?.toFixed(2)})</option>
                      <option value="full">Full Discount (Free)</option>
                      <option value="custom">Custom Amount</option>
                    </select>
                  </div>
                  
                  {formData.discount_type === 'custom' && (
                    <div>
                      <label className={labelClass}>Custom Monthly Price (PKR)</label>
                      <input 
                        type="number" 
                        value={formData.custom_price} 
                        onChange={(e) => setFormData({...formData, custom_price: e.target.value})}
                        className={inputClass}
                        placeholder="Enter amount" min="0" step="0.01" disabled={loading}
                      />
                    </div>
                  )}
                </div>
                
                {/* Effective Price Preview */}
                <div className="mt-3 p-3 bg-white rounded-lg border border-purple-200">
                  <p className="text-sm text-gray-600">
                    Base Price: <span className="font-medium">PKR {basePrice?.toFixed(2)}</span>
                  </p>
                  {formData.discount_type !== 'none' && (
                    <p className="text-sm text-purple-700">
                      Discount: <span className="font-medium">{formData.discount_type.replace('_', ' ')}</span>
                    </p>
                  )}
                  <p className="text-lg font-bold text-gray-900 mt-1">
                    Effective Monthly Price: <span className="text-green-600">PKR {effectivePrice?.toFixed(2)}</span>
                  </p>
                </div>
              </div>
            )}

            {/* Row 5: Dates & Status */}
            <div>
              <label className={labelClass}><Calendar size={12} className="inline mr-1"/> Expiry Date</label>
              <input 
                type="date" className={inputClass}
                value={formData.expiry_date} onChange={(e) => setFormData({...formData, expiry_date: e.target.value})}
                disabled={loading}
              />
            </div>
            <div>
              <label className={labelClass}><Activity size={12} className="inline mr-1"/> Status</label>
              <select 
                className={inputClass}
                value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value})}
                disabled={loading}
              >
                <option value="pending">Pending</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
                <option value="suspended">Suspended</option>
                <option value="churned">Churned</option>
              </select>
            </div>

            {/* Row 6: Financials */}
            <div>
              <label className={labelClass}><CreditCard size={12} className="inline mr-1"/> Due Balance (PKR)</label>
              <input 
                type="number" className={inputClass} min="0" step="0.01"
                value={formData.due_balance} onChange={(e) => setFormData({...formData, due_balance: e.target.value})}
                disabled={loading}
              />
            </div>
            <div>
              <label className={labelClass}><CreditCard size={12} className="inline mr-1"/> Credit Balance (PKR)</label>
              <input 
                type="number" className={inputClass} min="0" step="0.01"
                value={formData.credit_balance} onChange={(e) => setFormData({...formData, credit_balance: e.target.value})}
                disabled={loading}
              />
            </div>

          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-6 mt-2 border-t border-gray-100">
            <button 
              type="button" onClick={onClose} disabled={loading}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button 
              type="submit" disabled={loading}
              className="flex-1 px-4 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 shadow-md shadow-blue-200 transition-all flex items-center justify-center gap-2"
            >
              {loading ? <><Loader size={18} className="animate-spin" /> <span>Saving...</span></> : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCustomerModal;
